import React, { Component } from 'react';

import { buildPath } from '../utils/route_utils';
import { trackEvent } from '../utils/analytics';
import '../css/OpenSlideShow.css';

/*
 * Opening "NEXUNI letter converge" intro.
 *
 * Currently hidden. The implementation below (the X-split, the letter
 * converge, the logo settle and every matching keyframe in
 * OpenSlideShow.css) is kept intact — flip this flag back to true to
 * restore it. While it is false the hero paints immediately: video,
 * poster and copy are all visible on first frame.
 */
const INTRO_ANIMATION_ENABLED = false;

// Delays (seconds) used only while the intro plays.
const INTRO = {
  videoIn: 3.7,
  logoSettle: 3.7,
  announcement: 4.1,
  content: 3.9,
  complete: 5500,
};

class OpenSlideShow extends Component {
  constructor(props) {
    super(props);
    this.state = {
      current_lang: this.props.lang,
      animationComplete: !INTRO_ANIMATION_ENABLED,
      // Mobile gets the 720p encode (2.2 MB) instead of the 1080p pair.
      isCompact:
        typeof window !== 'undefined' &&
        typeof window.matchMedia === 'function' &&
        window.matchMedia('(max-width: 780px), (pointer: coarse)').matches,
      // With reduced motion the loop does not autoplay; the poster stands in.
      reduceMotion:
        typeof window !== 'undefined' &&
        typeof window.matchMedia === 'function' &&
        window.matchMedia('(prefers-reduced-motion: reduce)').matches,

      // H1 is set in English on every locale (founder-final). The
      // sub-line and proof line carry the language switch.
      valueProp: 'AI Robots, Ready to Work.',

      slideContent: {
        "zh": "Nexuni 耐思尼打造全自主 AI 機器人，為製造業與設施管理解決缺工問題，從巡邏、巡檢到焊接、清潔，提供全自動的機器人解決方案。",
        "en": "We build AI-powered robots that autonomously patrol, inspect, weld and clean for manufacturing and facility management.",
        "ja": "完全自律型AIロボットで、製造業と施設管理の人手不足を解決します。巡回・点検から溶接・清掃まで、すべて人の手を介さず稼働します。"
      },
      proofLine: {
        "zh": "已部署 100+ 套系統",
        "en": "100+ SYSTEMS DEPLOYED",
        "ja": "導入実績 100+ システム"
      },
      ctaText: {
        "zh": "預約現場評估",
        "en": "Book a site assessment",
        "ja": "現場アセスメントを予約"
      },
      announcementTag: {
        "zh": "最新",
        "en": "NEW",
        "ja": "新着"
      },
      announcement: {
        "zh": "Nexuni首度登黃仁勳GTC公布的台灣供應鏈名單",
        "en": "Nexuni debuts on Jensen Huang's GTC Taiwan supply chain list",
        "ja": "Nexuni、ジェンスン・フアン氏のGTC台湾サプライチェーン一覧に初登場"
      },
    }
  }

  componentDidMount() {
    if (INTRO_ANIMATION_ENABLED) {
      this.animTimer = setTimeout(() => {
        this.setState({ animationComplete: true });
      }, INTRO.complete);
    }
  }

  componentWillUnmount() {
    if (this.animTimer) clearTimeout(this.animTimer);
  }

  componentWillReceiveProps(nextProps) {
    if (nextProps.lang !== this.state.current_lang) {
      this.setState({ current_lang: nextProps.lang });
    }
  }

  // The proof line reads as a stat, not a sentence: the count is picked
  // out of whichever locale string it sits in (EN leads, zh/ja embed it).
  renderProofLine(text) {
    return text.split(/(100\+)/).filter(Boolean).map((part, i) =>
      part === '100+'
        ? <span className="hero-proof__num" key={i}>{part}</span>
        : <span key={i}>{part}</span>
    );
  }

  render() {
    const lang = this.state.current_lang;
    const intro = INTRO_ANIMATION_ENABLED;
    // Intro chrome unmounts once the sequence ends; it sits above the copy.
    const introVisible = intro && !this.state.animationComplete;

    const letters = [
      { id: 'n1', src: '/assets/mainpage_nexuni_n.svg', alt: 'N', startX: -35, startY: -30 },
      { id: 'e',  src: '/assets/mainpage_nexuni_e.svg', alt: 'E', startX: 25,  startY: -35 },
      { id: 'x',  src: '/assets/mainpage_nexuni_x.svg', alt: 'X', startX: -20, startY: 40 },
      { id: 'u',  src: '/assets/mainpage_nexuni_u.svg', alt: 'U', startX: 30,  startY: 35 },
      { id: 'n2', src: '/assets/mainpage_nexuni_n.svg', alt: 'N', startX: -40, startY: 15 },
      { id: 'i',  src: '/assets/mainpage_nexuni_i.svg', alt: 'I', startX: 38,  startY: -20 },
    ];

    return (
      <div className={'hero-section' + (intro ? ' hero-section--intro' : '')}>
        {/* Background video — full-bleed field footage (ODO on a live fab corridor) */}
        <div
          className="hero-video-bg"
          style={intro ? { animation: `fade-in 1.5s ease ${INTRO.videoIn}s forwards` } : undefined}
        >
          <video
            autoPlay={!this.state.reduceMotion}
            muted
            loop={!this.state.reduceMotion}
            playsInline
            preload="auto"
            aria-hidden="true"
            disablePictureInPicture
            disableRemotePlayback
            poster="/assets/hero/hero_poster.webp"
          >
            {this.state.isCompact ? (
              <>
                <source src="/video/hero_loop_720.webm" type="video/webm" />
                <source src="/video/hero_loop_720.mp4" type="video/mp4" />
              </>
            ) : (
              <>
                <source src="/video/hero_loop.webm" type="video/webm" />
                <source src="/video/hero_loop.mp4" type="video/mp4" />
              </>
            )}
          </video>
          {/* Ink scrim: light overall, concentrated into the lower-left
              corner so white copy clears the near-white fab floor. */}
          <div className="hero-video-overlay"></div>
          <div className="hero-video-scrim"></div>
        </div>

        {/* Subtle 45-degree grid pattern */}
        <div className="hero-grid-pattern"></div>

        {/* ---- Opening intro (hidden while INTRO_ANIMATION_ENABLED is false) ---- */}
        {introVisible && (
          <div className="hero-x-split">
            <img
              className="hero-x-left"
              src="/assets/mainpage_nexuni_opening_l.svg"
              alt=""
              style={{ animation: 'move-right 1.5s ease 0s forwards, fade-out 1.5s ease 0.75s forwards' }}
            />
            <img
              className="hero-x-right"
              src="/assets/mainpage_nexuni_opening_r.svg"
              alt=""
              style={{ animation: 'move-left 1.5s ease 0s forwards, fade-out 1.5s ease 0.75s forwards' }}
            />
          </div>
        )}

        {introVisible && (
          <div className="hero-logo-group" style={{ animation: `logo-settle 0.8s ease ${INTRO.logoSettle}s forwards` }}>
            <div className="hero-letters">
              {letters.map((letter) => (
                <div
                  className={"hero-letter-container" + (letter.id === 'i' ? " hero-letter-i" : "")}
                  key={letter.id}
                  style={{
                    '--start-x': letter.startX + 'vw',
                    '--start-y': letter.startY + 'vh',
                  }}
                >
                  <img
                    className="hero-letter-svg"
                    src={letter.src}
                    alt=""
                  />
                </div>
              ))}
            </div>
          </div>
        )}
        {/* ---- end opening intro ---- */}

        {/* Announcement pill pinned near the top of the video */}
        <a
          className="hero-announcement"
          href={buildPath(lang, '/about/news')}
          onClick={() => trackEvent('select_announcement', { location: 'hero' })}
          style={
            intro
              ? { animation: `hero-announcement-in 0.8s ease ${INTRO.announcement}s forwards, hero-announcement-pulse 2.2s ease-in-out ${INTRO.announcement + 0.9}s infinite` }
              : undefined
          }
        >
          <span className="hero-announcement__tag">{this.state.announcementTag[lang]}</span>
          <span className="hero-announcement__text">{this.state.announcement[lang]}</span>
          <span className="hero-announcement__arrow" aria-hidden="true">&rarr;</span>
        </a>

        {/* Hero copy — anchored lower-left over the footage */}
        <div
          className="hero-content"
          style={intro ? { animation: `hero-content-in 1s ease ${INTRO.content}s forwards` } : undefined}
        >
          <h1 className="hero-value-prop" lang="en">{this.state.valueProp}</h1>

          <p className="hero-sub-text">{this.state.slideContent[lang]}</p>

          <p className="hero-proof">{this.renderProofLine(this.state.proofLine[lang])}</p>

          <a
            className="hero-cta"
            href={buildPath(lang, '/contact')}
            onClick={() => trackEvent('select_cta', { location: 'hero', cta: 'site_assessment' })}
          >
            {this.state.ctaText[lang]}
            <span className="hero-cta__arrow" aria-hidden="true">&rarr;</span>
          </a>
        </div>
      </div>
    );
  }
}

export default OpenSlideShow;
